import { SagaIterator } from 'redux-saga';
import { all, call, delay, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  actions,
  AppealCorrectionsPayload,
  AppealRequestPayload,
  RequestIdPayload,
  SendAppealPayload,
} from './ducks';
import { selectors } from './selectors';
import { ModelState } from '@features/PersonalArea/types';
import {
  createAppeal,
  CreateAppealRequest,
  getUserDocumentsApi,
  getUserAppealDocumentsApi,
  returnAppealFromCorrectionApi,
  getUserRequestDataApi,
  getPdfApi,
  getUserRequestScoreApi,
  getInvalidDocumentsApi,
  getUserAppealApi,
  updateAppealApi,
  fixBankDetailsApi,
} from '@features/PersonalArea/model/api';

import { userModel } from '@entities/user';

import {
  mapEditedDataFromServerToUi,
  mapUserDocumentsFromServerToUI,
} from '@features/PersonalArea/mapper';
import { push } from 'react-router-redux';
import { globalConfig } from '@/globalConfig';
import { loaderModel } from '@entities/loader';
import { personalInfoModel } from '@features/Registration';
import { mapBankDetailsFromUiToServer } from '@features/Registration/mapper';
import { composeSaga, passActionPayload } from '@common/store-utils';
import saveAs from 'file-saver';
import { settingsModel } from '@entities/settings';
import { persistRequestId } from '../utils/persistRequestId';

function* resolveRequestId(
  payloadRequestId?: number | string | null
): SagaIterator<number | string | null> {
  let requestId =
    payloadRequestId ?? (yield select(userModel.selectors.requestId));
  if (!requestId) {
    requestId = yield call(
      [window.localStorage, window.localStorage.getItem],
      globalConfig.storage.requestId
    );
  }
  return requestId || null;
}

function* syncRequestContext(requestId: number | string): SagaIterator {
  const requestIdStr = String(requestId);
  const numericId = Number(requestIdStr);
  if (!Number.isNaN(numericId)) {
    yield call(persistRequestId, numericId);
  } else {
    yield call(
      [window.localStorage, window.localStorage.setItem],
      globalConfig.storage.requestId,
      requestIdStr
    );
  }
  yield put(userModel.actions.setRequestId(requestIdStr));
}

function* sendAppealForm(
  action: PayloadAction<SendAppealPayload | undefined>
): SagaIterator {
  yield put(actions.setAppealSubmitError(''));

  const appealState: ModelState = yield select(selectors.appeal);
  const details = action.payload?.details ?? appealState.details;
  const documents = action.payload?.documents ?? appealState.documents;

  const requestId = yield call(resolveRequestId, action.payload?.requestId);

  if (!requestId) {
    yield put(
      actions.setAppealSubmitError(
        'לא ניתן להגיש ערעור — מזהה בקשה חסר. נסו שוב או חזרו לאזור האישי.'
      )
    );
    return;
  }

  const isAppealsOpen = yield select(settingsModel.selectors.isAppealsOpen);
  const appealType = yield select(selectors.appealType);
  const shouldDisableAppeals = !isAppealsOpen;

  const createAppealRequest: CreateAppealRequest = {
    user_request_id: Number(requestId),
    details: details || '',
    documents: JSON.stringify(documents ?? []),
    type: appealType,
  };

  yield put(loaderModel.actions.setIsLoading(true));

  try {
    if (shouldDisableAppeals) {
      yield put(actions.setIsAppealSentSuccess(false));
      yield put(
        actions.setAppealSubmitError(
          'לא ניתן להגיש ערעור בשלב זה. נסו שוב מאוחר יותר.'
        )
      );
      return;
    }

    const response: number | null = yield call(
      createAppeal,
      createAppealRequest
    );
    yield put(actions.setIsAppealSentSuccess(false));

    const isSuccess =
      typeof response === 'number' && response >= 200 && response < 300;
    if (isSuccess) {
      yield put(actions.setAppealSubmitError(''));
      yield put(actions.setIsAppealSent(true));
      yield put(actions.setIsAppealSentSuccess(true));
      yield put(push(globalConfig.routes.personalArea()));
    } else {
      yield put(
        actions.setAppealSubmitError('שליחת הערעור נכשלה. נסו שוב.')
      );
    }
  } catch (e) {
    yield put(actions.setIsAppealSentSuccess(false));
    yield put(
      actions.setAppealSubmitError('שליחת הערעור נכשלה. נסו שוב.')
    );
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

/**
 * Get user documents Saga
 * @returns {void}
 */
function* getUserDocumentsListSaga(
  action: PayloadAction<RequestIdPayload | undefined>
): SagaIterator {
  const requestId = yield call(resolveRequestId, action.payload?.requestId);
  if (requestId) {
    yield call(syncRequestContext, requestId);
  }
  const response = yield call(getUserDocumentsApi);
  if (response) {
    yield put(
      actions.setUserDocuments(mapUserDocumentsFromServerToUI(response))
    );
  }
}

function* getAppealDocumentsToFixSaga(
  action: PayloadAction<AppealRequestPayload | undefined>
): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));
  const userStatus =
    action.payload?.requestStatus ??
    (yield select(userModel.selectors.requestStatus));
  const requestId = yield call(resolveRequestId, action.payload?.requestId);
  try {
    if (!requestId) {
      return;
    }
    yield call(syncRequestContext, requestId);

    if (!!userStatus && userStatus === 'APPEAL_REPAIR_BACK_FOR') {
      try {
        const userData = yield call(getUserAppealDocumentsApi, requestId);
        yield put(actions.setAppealDocumentsBackToFix(userData));
      } catch (e) {
        console.log(e);
      }
      const appeal = yield call(getUserAppealApi, requestId);
      yield put(actions.setAppealReturnComment(appeal?.return_comment || ''));
      yield put(actions.setAppealOriginalDocuments(appeal?.documents || []));
      yield put(actions.setAppealType(appeal?.type || ''));
      yield put(actions.setDetails(appeal?.details || ''));
    } else if (!!userStatus && userStatus === 'APPEAL_SUBMITTED') {
      const appeal = yield call(getUserAppealApi, requestId);
      yield put(actions.setAppealReturnComment(appeal?.return_comment || ''));
      yield put(actions.setAppealOriginalDocuments(appeal?.documents || []));
      yield put(actions.setAppealType(appeal?.type || ''));
      yield put(actions.setDetails(appeal?.details || ''));
    }
  } catch (e) {
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* getUserRequestDetailsSaga(
  action: PayloadAction<RequestIdPayload | undefined>
): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));
  const requestId = yield call(resolveRequestId, action.payload?.requestId);
  if (!requestId) {
    yield put(loaderModel.actions.setIsLoading(false));
    return;
  }

  yield call(syncRequestContext, requestId);
  const requestIdStr = String(requestId);

  try {
    const requestData = yield call(getUserRequestDataApi);
    if (requestData) {
      yield put(
        actions.setRequestData(mapEditedDataFromServerToUi(requestData))
      );
    }
    const requestScore = yield call(
      getUserRequestScoreApi,
      Number(requestIdStr)
    );
    if (requestScore) {
      yield put(actions.setDetailedScore(requestScore));
    }
  } catch (e) {
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* getUserInvalidDocumentsSaga(
  action: PayloadAction<RequestIdPayload | undefined>
): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));
  try {
    const requestId = yield call(resolveRequestId, action.payload?.requestId);
    if (requestId) {
      yield call(syncRequestContext, requestId);
    }
    const invalidDocuments = yield call(getInvalidDocumentsApi);
    yield put(actions.setInvalidDocuments(invalidDocuments));
  } catch (e) {
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* getPdfSaga(): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));
  try {
    const response = yield call(getPdfApi);
    const file = new Blob([response]);
    yield call(saveAs, file, 'milgo.pdf');
  } catch (e) {
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* fixBankDetailsSaga(): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));
  const userData = yield select(personalInfoModel.selectors.bankDetails);
  const requestId = yield select(userModel.selectors.requestId);
  const file = yield select(selectors.fixBankFile);
  try {
    const response = yield call(fixBankDetailsApi, requestId, {
      ...mapBankDetailsFromUiToServer(userData),
      bank_account_reference: file,
    });

    if (!!response) {
      window.location.reload();
    }
  } catch (e) {
    yield put(actions.setIsChangeBankDetails(false));
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(actions.setIsChangeBankDetails(false));
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* returnDocumentsFromCorrectionsSaga(
  props: AppealCorrectionsPayload
): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));
  const { requestId: payloadRequestId, ...corrections } = props || {};
  const requestId = yield call(resolveRequestId, payloadRequestId);
  try {
    if (!requestId) {
      return;
    }
    const response = yield call(
      returnAppealFromCorrectionApi,
      requestId,
      corrections
    );
    if (!!response) {
      yield put(push(globalConfig.routes.personalArea()));
      yield delay(1000);
      yield call(window.location.reload);
    }
  } catch (e) {
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* updateAppealSaga(
  action: PayloadAction<RequestIdPayload | undefined>
): SagaIterator {
  const { documents, details, appealType }: ModelState = yield select(
    selectors.appeal
  );
  const requestId = yield call(resolveRequestId, action.payload?.requestId);
  if (!requestId) {
    return;
  }

  const updateAppealRequest: Partial<CreateAppealRequest> = {
    details,
    type: !!appealType ? appealType : 'PERSONAL_PARAMETER_CHANGE',
    documents: JSON.stringify(documents),
  };
  yield put(loaderModel.actions.setIsLoading(true));

  try {
    const response = yield call(
      updateAppealApi,
      requestId,
      updateAppealRequest
    );
    yield put(actions.setIsAppealSentSuccess(false));
    if (!!response) {
      yield put(push(globalConfig.routes.main()));
    }
  } catch (e) {
    yield put(loaderModel.actions.setIsLoading(false));
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* watcher(): SagaIterator<void> {
  yield all([
    takeEvery(actions.createSendAppealForm, sendAppealForm),
    takeEvery(actions.getPdf, getPdfSaga),
    takeEvery(actions.getUserDocumentsList, getUserDocumentsListSaga),
    takeEvery(actions.getAppealDocumentsBackToFix, getAppealDocumentsToFixSaga),
    takeEvery(actions.returnDetailsFromCorrections, fixBankDetailsSaga),
    takeEvery(actions.getRequestData, getUserRequestDetailsSaga),
    takeEvery(actions.getInvalidDocuments, getUserInvalidDocumentsSaga),
    takeEvery(actions.updateAppeal, updateAppealSaga),
    takeEvery(
      actions.returnAppealDocumentsFromCorrections,
      composeSaga(returnDocumentsFromCorrectionsSaga, [passActionPayload])
    ),
  ]);
}

export const sagas = {
  watcher,
};
